/**
 * Stripe configuration and initialization
 * Handles Stripe SDK setup and environment configuration
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { detectCurrency } from '../../utils/currency';
import { PRICING_DATA } from '../../utils/constants';

function dbg(..._args: any[]) {
  // Temporary debug logging disabled for production builds
  // Uncomment the line below for local debugging:
  // console.log('[Stripe Config]', ...args);
}

// Stripe configuration
const STRIPE_CONFIG = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  apiVersion: '2023-10-16' as const,
};

// Stripe instance (singleton)
let stripePromise: Promise<Stripe | null>;

/**
 * Get Stripe instance
 * Returns a promise that resolves to the Stripe object
 */
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    if (!STRIPE_CONFIG.publishableKey) {
      console.error('[Stripe] CRITICAL: No publishable key found. Check REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable.');
      dbg('No publishable key found in env');
      return Promise.resolve(null);
    }

    dbg('Initializing Stripe with publishable key:', STRIPE_CONFIG.publishableKey.substring(0, 20) + '...');
    stripePromise = loadStripe(STRIPE_CONFIG.publishableKey, {
      apiVersion: STRIPE_CONFIG.apiVersion,
    });
  }

  return stripePromise;
};

/**
 * Stripe price IDs for different subscription tiers
 * Resolves to the correct currency-specific price IDs based on detected location
 */
const _curr = detectCurrency();
export const STRIPE_PRICE_IDS = {
  STANDARD: PRICING_DATA.standard[_curr].priceId || 'price_standard',
  PREMIUM: PRICING_DATA.premium[_curr].priceId || 'price_premium',
  TWELVE_MONTH_PACKAGE: PRICING_DATA.twelve_month_package[_curr].priceId || 'price_twelve_month',
};

/**
 * Subscription tier mapping
 */
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  STANDARD: 'standard',
  PREMIUM: 'premium',
  ANNUAL: 'annual',
  TWELVE_MONTH_PACKAGE: 'twelve_month_package',
  CORPORATE: 'corporate',
  STACKCOMMERCE_LIFETIME: 'stackcommerce_lifetime',
} as const;

export type SubscriptionTier = typeof SUBSCRIPTION_TIERS[keyof typeof SUBSCRIPTION_TIERS];

/**
 * Stripe webhook endpoints
 */
export const STRIPE_WEBHOOKS = {
  SUBSCRIPTION_CREATED: 'customer.subscription.created',
  SUBSCRIPTION_UPDATED: 'customer.subscription.updated',
  SUBSCRIPTION_DELETED: 'customer.subscription.deleted',
  INVOICE_PAYMENT_SUCCEEDED: 'invoice.payment_succeeded',
  INVOICE_PAYMENT_FAILED: 'invoice.payment_failed',
} as const;

/**
 * Stripe customer portal configuration
 */
export const CUSTOMER_PORTAL_CONFIG = {
  returnUrl: `${window.location.origin}/dashboard/billing`,
  features: {
    payment_method_update: { enabled: true },
    subscription_cancel: { enabled: true },
    subscription_pause: { enabled: true },
    subscription_update: { enabled: true },
    invoice_history: { enabled: true },
  },
};

const stripeConfig = {
  getStripe,
  STRIPE_PRICE_IDS,
  SUBSCRIPTION_TIERS,
  STRIPE_WEBHOOKS,
  CUSTOMER_PORTAL_CONFIG,
};

export default stripeConfig;
